import { Box, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface BannerContent {
    icon: string;
    text: string;
}

const TransactionContextBanner: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [searchParams] = useSearchParams();
    
    const isSmartCheckContext = searchParams.get('smartCheckContext') === 'true';
    const issueType = searchParams.get('issueType');
    const asset = searchParams.get('asset');
    const missingPriceOnly = searchParams.get('missingPriceOnly') === 'true';
    
    const bannerContent = useMemo<BannerContent | null>(() => {
        if (!isSmartCheckContext) {
            // Default state when not from Smart Check
            return {
                icon: '💡',
                text: t('transactions.banner.manageTip', 'Manage your transactions or use filters to find specific entries')
            };
        }
        
        if (issueType === 'missingPrice') {
            return {
                icon: '🔧',
                text: missingPriceOnly 
                    ? t('transactions.banner.fixMissingPriceFiltered', "You're viewing transactions with missing prices. Please add a price, change the asset, or make another edit to fix them.")
                    : t('transactions.banner.fixMissingPrice', 'Fix missing prices: Please add a price, change the asset, or make another edit to fix them.')
            };
        }
        
        if (issueType === 'costBasis') {
            return {
                icon: '📊',
                text: t('transactions.banner.reviewCostBasis', 'Review {{asset}} transaction history - Check gaps identified by AI analysis', { asset: asset || 'asset' })
            };
        }
        
        // Fallback for Smart Check context without specific issue type
        return {
            icon: '🔍',
            text: t('transactions.banner.smartCheckContext', 'Reviewing transactions from Smart Check analysis')
        };
    }, [isSmartCheckContext, issueType, asset, missingPriceOnly, t]);
    
    if (!bannerContent) {
        return null;
    }
    
    return (
        <Box
            sx={{
                flexGrow: 1,
                backgroundColor: (theme) => 
                    theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.02)'
                        : 'rgba(0, 0, 0, 0.02)',
                border: (theme) => 
                    `1px solid ${theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'rgba(0, 0, 0, 0.08)'}`,
                borderRadius: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mr: 2,
            }}
        >
            <Typography
                variant="body1"
                sx={{
                    fontSize: '1rem',
                    fontWeight: 450,
                    color: theme.palette.text.primary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <span>{bannerContent.icon}</span>
                {bannerContent.text}
            </Typography>
        </Box>
    );
};

export default TransactionContextBanner;